// src/pages/authn/SecurityPage.tsx
// 2FA status, password, connected accounts, security events.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useAuthn, useAuthnAuth, useAuthn2FA, useAuth, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, Badge, ErrorAlert } from '@react/components';
import { QRCodeSVG } from 'qrcode.react';
import { Timeline } from '../../components/Timeline';
import type { TimelineEvent } from '../../components/Timeline';
import { PasswordStrength } from '../../components/PasswordStrength';
import { OtpInput } from '../../components/OtpInput';
import { validateUpdatePassword, calculatePasswordStrength, passwordStrengthLabels, formatLoginResult, formatSessionInfo } from '../../features/authn/logic';
import type { OAuth2Connection, OAuth2Provider, LoginRecord, TwoFactorMethod, OIDCClaims } from '@core/types/authn';
import type { PaginatedRequest } from '@core/types/pagination';

export const SecurityPage = observer(() => {
  const authnStore = useAuthn();
  const authnAuth = useAuthnAuth();
  const twoFA = useAuthn2FA();
  const auth = useAuth();
  const { t } = useI18n();

  const [connections, setConnections] = useState<OAuth2Connection[]>([]);
  const [providers, setProviders] = useState<OAuth2Provider[]>([]);
  const [error, setError] = useState<unknown>(null);

  // Email verification
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState<unknown>(null);

  // "What does my account expose to connected apps" claims preview (OIDC UserInfo)
  const [claims, setClaims] = useState<OIDCClaims | null>(null);
  const [claimsOpen, setClaimsOpen] = useState(false);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsError, setClaimsError] = useState<unknown>(null);

  // 2FA setup
  type TwoFAStep = 'method' | 'scan' | 'verify' | 'backup';
  const [tfaStep, setTfaStep] = useState<TwoFAStep>('method');
  const [tfaMethod, setTfaMethod] = useState<TwoFactorMethod>('TWO_FACTOR_METHOD_TOTP');
  const [tfaSecret, setTfaSecret] = useState('');
  const [tfaQrUrl, setTfaQrUrl] = useState('');
  const [tfaBackupCodes, setTfaBackupCodes] = useState<string[]>([]);
  const [tfaCode, setTfaCode] = useState('');
  const [tfaError, setTfaError] = useState<unknown>(null);

  // Password change
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', passwordConfirmation: '' });
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({});
  const [pwError, setPwError] = useState<unknown>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  useEffect(() => {
    authnStore.ListOAuth2Connections()
      .then((resp) => setConnections(resp.connections?.items ?? []))
      .catch(() => {});
    authnStore.ListOAuth2Providers()
      .then((resp) => setProviders(resp.providers?.items ?? []))
      .catch(() => {});
    authnAuth.ListLoginRecords({ pagination: { limit: 10, cursor: '', direction: '' } } as PaginatedRequest);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePasswordChange = async () => {
    const validation = validateUpdatePassword(pwForm);
    if (Object.keys(validation).length > 0) {
      setPwErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      await authnAuth.UpdatePassword({
        old_password: pwForm.oldPassword,
        new_password: pwForm.newPassword,
        password_confirmation: pwForm.passwordConfirmation,
      });
      setPwSuccess(true);
      setPwForm({ oldPassword: '', newPassword: '', passwordConfirmation: '' });
    } catch (err) {
      setPwError(err);
    }
  };

  const handleDisable2FA = async () => {
    try {
      await twoFA.DisableTwoFactorAuth({ entity: {} as never, password: '' });
    } catch (err) {
      setError(err);
    }
  };

  const handleSendVerificationEmail = async () => {
    if (!auth.user?.email) return;
    setSendingVerification(true);
    setVerificationError(null);
    try {
      await authnAuth.SendVerificationEmail({ email: auth.user.email });
      setVerificationSent(true);
    } catch (err) {
      setVerificationError(err);
    } finally {
      setSendingVerification(false);
    }
  };

  const handleTogglePreviewClaims = async () => {
    if (claimsOpen) {
      setClaimsOpen(false);
      return;
    }
    setClaimsOpen(true);
    if (claims) return;
    setClaimsLoading(true);
    setClaimsError(null);
    try {
      const resp = await authnStore.GetUserInfo();
      setClaims(resp.claims ?? null);
    } catch (err) {
      setClaimsError(err);
    } finally {
      setClaimsLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setTfaError(null);
    try {
      const resp = await twoFA.EnableTwoFactorAuth({ entity: {} as never, method: tfaMethod });
      setTfaSecret(resp.secret);
      setTfaQrUrl(resp.qr_code_url);
      setTfaBackupCodes(resp.backup_codes?.items ?? []);
      const isTotpMethod = tfaMethod === 'TWO_FACTOR_METHOD_TOTP';
      setTfaStep(isTotpMethod ? 'scan' : 'verify');
    } catch (err) {
      setTfaError(err);
    }
  };

  const handleVerify2FA = async () => {
    setTfaError(null);
    try {
      await twoFA.VerifyTwoFactorAuth({ entity: {} as never, code: tfaCode });
      setTfaStep('backup');
    } catch (err) {
      setTfaError(err);
    }
  };

  const handleDone2FA = () => {
    setTfaStep('method');
    setTfaCode('');
    setTfaSecret('');
    setTfaQrUrl('');
    setTfaBackupCodes([]);
  };

  const handleReset2FA = () => {
    setTfaStep('method');
    setTfaCode('');
    setTfaError(null);
  };

  const tfaMethods = [
    { value: 'TWO_FACTOR_METHOD_TOTP', labelKey: 'auth.2faMethodTotp', desc: 'auth.2faTotpDesc' },
    { value: 'TWO_FACTOR_METHOD_SMS', labelKey: 'auth.2faMethodSms', desc: 'auth.2faSmsDesc' },
    { value: 'TWO_FACTOR_METHOD_EMAIL', labelKey: 'auth.2faMethodEmail', desc: 'auth.2faEmailDesc' },
  ];

  const handleUnlink = async (providerId: string) => {
    try {
      await authnStore.UnlinkOAuth2Account({ entity: {} as never, provider_id: providerId });
      setConnections((prev) => prev.filter((c) => c.provider?.id !== providerId));
    } catch (err) {
      setError(err);
    }
  };

  const handleLink = async (providerId: string) => {
    try {
      await authnStore.LinkOAuth2Account({
        entity: {} as never,
        provider_id: providerId,
        redirect_uri: `${window.location.origin}/oauth/callback`,
      });
      const resp = await authnStore.ListOAuth2Connections();
      setConnections(resp.connections?.items ?? []);
    } catch (err) {
      setError(err);
    }
  };

  const connectedProviderIds = new Set(connections.map((c) => c.provider?.id));
  const availableProviders = providers.filter((p) => p.enabled && !connectedProviderIds.has(p.entity?.id));

  const securityEvents: TimelineEvent[] = (authnAuth.items as LoginRecord[]).slice(0, 5).map((record) => {
    const result = formatLoginResult(record.result ?? '');
    const info = formatSessionInfo(record.user_agent ?? '');
    return {
      id: record.entity?.id ?? '',
      title: t(result.labelKey),
      description: `${info.browser} / ${info.os} — ${record.ip_address ?? ''}`,
      time: record.timestamp ?? '',
      variant: result.variant === 'success' ? 'success' : result.variant === 'error' ? 'error' : 'default',
    } as TimelineEvent;
  });

  const is2FAEnabled = auth.user?.two_factor_enabled;
  const pwScore = calculatePasswordStrength(pwForm.newPassword);

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('security.title')}</h1>

      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
      {pwSuccess && <Alert variant="success" dismissible onDismiss={() => setPwSuccess(false)}>{t('security.passwordChanged')}</Alert>}

      {/* Email verification */}
      {auth.user && !auth.user.email_verified && (
        <Alert variant="warning">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span>{t('security.emailNotVerified')}</span>
            {verificationSent ? (
              <span className="text-sm font-medium">{t('security.verificationEmailSent')}</span>
            ) : (
              <Button size="sm" variant="secondary" onClick={handleSendVerificationEmail} isLoading={sendingVerification}>
                {t('security.sendVerificationEmail')}
              </Button>
            )}
          </div>
          {!!verificationError && (
            <div className="mt-2">
              <ErrorAlert error={verificationError} />
            </div>
          )}
        </Alert>
      )}

      {/* 2FA + Password */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Two-factor authentication */}
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('security.twoFactorAuth')}</CardTitle>
              <Badge variant={is2FAEnabled ? 'success' : 'warning'}>
                {is2FAEnabled ? t('security.2faEnabled') : t('security.2faDisabled')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 flex-1">
            {is2FAEnabled ? (
              <Button variant="danger" size="sm" onClick={handleDisable2FA}>{t('security.disable2fa')}</Button>
            ) : (
              <>
                {tfaError && <ErrorAlert error={tfaError} dismissible onDismiss={() => setTfaError(null)} />}

                {tfaStep === 'method' && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">{t('auth.2faSetupSelectMethod')}</p>
                    {tfaMethods.map((m) => (
                      <label
                        key={m.value}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          tfaMethod === m.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tfaMethod"
                          value={m.value}
                          checked={tfaMethod === m.value}
                          onChange={(e) => setTfaMethod(e.target.value as TwoFactorMethod)}
                          className="text-primary"
                        />
                        <div>
                          <div className="text-sm font-medium">{t(m.labelKey)}</div>
                          <div className="text-xs text-muted-foreground">{t(m.desc)}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {tfaStep === 'scan' && (
                  <div className="space-y-4 text-center">
                    <p className="text-sm text-muted-foreground">{t('auth.2faSetupScan')}</p>
                    {tfaQrUrl && <QRCodeSVG value={tfaQrUrl} size={192} className="mx-auto rounded-lg" />}
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">{t('auth.2faManualEntry')}</p>
                      <code className="text-sm font-mono text-foreground select-all break-all">{tfaSecret}</code>
                    </div>
                  </div>
                )}

                {tfaStep === 'verify' && (
                  <div className="space-y-4 text-center">
                    <p className="text-sm text-muted-foreground">{t('auth.2faEnterCode')}</p>
                    <OtpInput value={tfaCode} onChange={setTfaCode} />
                  </div>
                )}

                {tfaStep === 'backup' && (
                  <div className="space-y-4">
                    <Alert variant="warning">{t('auth.2faSaveBackupCodes')}</Alert>
                    <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg">
                      {tfaBackupCodes.map((code, i) => (
                        <code key={i} className="text-sm font-mono text-foreground">{code}</code>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
          {!is2FAEnabled && (
            <CardFooter className="flex justify-end gap-2">
              {tfaStep === 'method' && (
                <Button onClick={handleEnable2FA} isLoading={twoFA.isLoading}>{t('security.enable2fa')}</Button>
              )}
              {tfaStep === 'scan' && (
                <>
                  <Button variant="secondary" onClick={handleReset2FA}>{t('common.back')}</Button>
                  <Button onClick={() => setTfaStep('verify')}>{t('common.next')}</Button>
                </>
              )}
              {tfaStep === 'verify' && (
                <>
                  <Button variant="secondary" onClick={() => setTfaStep(tfaMethod === 'TWO_FACTOR_METHOD_TOTP' ? 'scan' : 'method')}>{t('common.back')}</Button>
                  <Button onClick={handleVerify2FA} isLoading={twoFA.isLoading} disabled={tfaCode.length < 6}>
                    {t('auth.verifyCode')}
                  </Button>
                </>
              )}
              {tfaStep === 'backup' && (
                <Button fullWidth onClick={handleDone2FA}>{t('auth.2faDone')}</Button>
              )}
            </CardFooter>
          )}
        </Card>

        {/* Password change */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="text-base">{t('security.changePassword')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 flex-1">
            {pwError ? <ErrorAlert error={pwError} dismissible onDismiss={() => setPwError(null)} /> : null}
            <Input
              label={t('security.currentPassword')}
              type="password"
              value={pwForm.oldPassword}
              onChange={(e) => setPwForm((p) => ({ ...p, oldPassword: e.target.value }))}
              error={pwErrors.oldPassword}
            />
            <div className="space-y-1">
              <div>
                <Input
                  label={t('auth.newPassword')}
                  type="password"
                  value={pwForm.newPassword}
                  onChange={(e) => setPwForm((p) => ({ ...p, newPassword: e.target.value }))}
                  error={pwErrors.newPassword}
                />
                <div className={`mt-2${pwForm.newPassword ? '' : ' invisible'}`}>
                  <PasswordStrength score={pwScore} label={t(passwordStrengthLabels[pwScore])} />
                </div>
              </div>
              <Input
                label={t('auth.confirmPassword')}
                type="password"
                value={pwForm.passwordConfirmation}
                onChange={(e) => setPwForm((p) => ({ ...p, passwordConfirmation: e.target.value }))}
                error={pwErrors.passwordConfirmation}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button onClick={handlePasswordChange} isLoading={authnAuth.isLoading}>
              {t('common.save')}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Connected accounts */}
      <Card>
        <CardHeader>
          <CardTitle>{t('security.connectedAccounts')}</CardTitle>
          <CardDescription>{t('security.connectedAccountsDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {connections.map((conn) => (
            <div key={conn.entity?.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <p className="text-sm font-medium text-foreground">{conn.external_username || conn.external_user_id}</p>
                <p className="text-xs text-muted-foreground">{conn.provider?.id}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleUnlink(conn.provider?.id ?? '')}>
                {t('security.disconnect')}
              </Button>
            </div>
          ))}
          {connections.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('security.noConnections')}</p>
          )}
        </CardContent>
      </Card>

      {/* Available providers */}
      {availableProviders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('security.availableProviders')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {availableProviders.map((p) => (
                <Button key={p.entity?.id} variant="secondary" className="justify-start" onClick={() => handleLink(p.entity?.id ?? '')}>
                  {p.provider_name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent security events */}
      {securityEvents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('security.recentEvents')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Timeline events={securityEvents} />
          </CardContent>
        </Card>
      )}

      {/* What connected apps see about me — OIDC UserInfo preview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('security.claimsPreviewTitle')}</CardTitle>
              <CardDescription>{t('security.claimsPreviewDesc')}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleTogglePreviewClaims} isLoading={claimsLoading}>
              {claimsOpen ? t('common.hide') : t('common.show')}
            </Button>
          </div>
        </CardHeader>
        {claimsOpen && (
          <CardContent>
            {!!claimsError && <ErrorAlert error={claimsError} dismissible onDismiss={() => setClaimsError(null)} />}
            {claims && (
              <dl className="divide-y divide-border">
                {Object.entries(claims)
                  .filter(([, v]) => v !== '' && v !== undefined && v !== null)
                  .map(([key, value]) => (
                    <div key={key} className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{key}</dt>
                      <dd className="mt-0.5 text-sm text-foreground font-mono break-all sm:col-span-2 sm:mt-0">{String(value)}</dd>
                    </div>
                  ))}
              </dl>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
});

export default SecurityPage;
